import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  CheckCircle,
  AlertTriangle,
  Clock,
  MapPin,
  User,
  Calendar,
  Search,
  Filter,
  Eye,
  Download
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface AlertEvent {
  id: string;
  rule_id: string;
  rule_name: string;
  device_name: string;
  vehicle_plate?: string;
  happened_at: string;
  payload: Record<string, any>;
  ack_by?: string | null;
  ack_at?: string | null;
  priority: 'high' | 'medium' | 'low';
}

interface AlertEventsManagerProps {
  events: AlertEvent[];
  onAcknowledge: (eventId: string, userId: string) => void;
}

export function AlertEventsManager({ events, onAcknowledge }: AlertEventsManagerProps) {
  const [selectedEvent, setSelectedEvent] = useState<AlertEvent | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const filteredEvents = events.filter(event => {
    const matchesSearch = !searchFilter || 
      event.device_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      event.vehicle_plate?.toLowerCase().includes(searchFilter.toLowerCase()) ||
      event.rule_name.toLowerCase().includes(searchFilter.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'pending' && !event.ack_at) ||
      (statusFilter === 'acknowledged' && event.ack_at);
    
    const matchesPriority = priorityFilter === 'all' || event.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleAcknowledge = (eventId: string) => {
    // In a real app, get the current user ID
    const currentUserId = 'current-user';
    onAcknowledge(eventId, currentUserId);
    setSelectedEvent(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-destructive text-destructive-foreground';
      case 'medium': return 'bg-warning text-warning-foreground';
      case 'low': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  const formatEventPayload = (payload: Record<string, any>) => {
    const items = [];
    
    if (payload.speed_kmh && payload.limit) {
      items.push(`Velocidade: ${payload.speed_kmh} km/h (limite: ${payload.limit} km/h)`);
    }
    
    if (payload.battery_level && payload.threshold) {
      items.push(`Bateria: ${payload.battery_level}% (limite: ${payload.threshold}%)`);
    }
    
    if (payload.geofence && payload.action) {
      items.push(`${payload.action === 'entered' ? 'Entrou em' : 'Saiu de'}: ${payload.geofence}`);
    }
    
    if (payload.location) {
      items.push(`Local: ${payload.location}`);
    }
    
    return items.length > 0 ? items.join(' | ') : 'Detalhes do evento';
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Dispositivo, veículo ou regra..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="acknowledged">Confirmados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Prioridade</label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="medium">Média</SelectItem>
                  <SelectItem value="low">Baixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Ações</label>
              <Button variant="outline" className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      <div className="space-y-3">
        {filteredEvents.map((event) => (
          <Card key={event.id} className={`border-l-4 ${
            event.priority === 'high' ? 'border-l-destructive' :
            event.priority === 'medium' ? 'border-l-warning' :
            'border-l-muted-foreground'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className={getPriorityColor(event.priority)}>
                      {getPriorityLabel(event.priority)}
                    </Badge>
                    {event.ack_at ? (
                      <Badge variant="outline" className="text-success border-success">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Confirmado
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-warning border-warning">
                        <Clock className="h-3 w-3 mr-1" />
                        Pendente
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(event.happened_at), { 
                        addSuffix: true,
                        locale: ptBR 
                      })}
                    </span>
                  </div>
                  
                  <div>
                    <h4 className="font-medium">{event.rule_name}</h4>
                    <div className="text-sm text-muted-foreground">
                      <span className="font-medium">{event.device_name}</span>
                      {event.vehicle_plate && (
                        <span> • Veículo: {event.vehicle_plate}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm">
                    {formatEventPayload(event.payload)}
                  </div>
                  
                  {event.ack_at && event.ack_by && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Confirmado por {event.ack_by} em {new Date(event.ack_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setSelectedEvent(event)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Detalhes do Evento</DialogTitle>
                      </DialogHeader>
                      {selectedEvent && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium">Regra</label>
                              <p className="text-sm">{selectedEvent.rule_name}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Dispositivo</label>
                              <p className="text-sm">{selectedEvent.device_name}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Veículo</label>
                              <p className="text-sm">{selectedEvent.vehicle_plate || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium">Prioridade</label>
                              <Badge className={getPriorityColor(selectedEvent.priority)}>
                                {getPriorityLabel(selectedEvent.priority)}
                              </Badge>
                            </div>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium">Data/Hora</label>
                            <p className="text-sm">
                              {new Date(selectedEvent.happened_at).toLocaleString()}
                            </p>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium">Detalhes</label>
                            <div className="text-sm bg-muted p-3 rounded">
                              {formatEventPayload(selectedEvent.payload)}
                            </div>
                          </div>
                          
                          {selectedEvent.ack_at ? (
                            <div className="p-3 bg-success/10 border border-success/20 rounded">
                              <div className="flex items-center gap-2 text-success">
                                <CheckCircle className="h-4 w-4" />
                                <span className="font-medium">Evento confirmado</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                Por {selectedEvent.ack_by} em {new Date(selectedEvent.ack_at).toLocaleString()}
                              </p>
                            </div>
                          ) : (
                            <Button 
                              onClick={() => handleAcknowledge(selectedEvent.id)}
                              className="w-full"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Confirmar Evento
                            </Button>
                          )}
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                  
                  {!event.ack_at && (
                    <Button 
                      size="sm"
                      onClick={() => handleAcknowledge(event.id)}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Confirmar
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {filteredEvents.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum evento encontrado</h3>
              <p className="text-muted-foreground">
                {events.length === 0 
                  ? 'Não há eventos de alerta registrados ainda.'
                  : 'Nenhum evento corresponde aos filtros aplicados.'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}